import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CRED_LABELS, CRED_TYPES, formatDate } from "@/lib/constants";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";
import { CloudArrowUp, File as FileIcon, Sparkle, FloppyDisk, CheckCircle, X } from "@phosphor-icons/react";

export default function UploadOCR() {
  const [practitioners, setPractitioners] = useState([]);
  const [selectedPrac, setSelectedPrac] = useState("");
  const [uploading, setUploading] = useState(false);
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/practitioners").then((r) => setPractitioners(r.data)).catch(() => {});
  }, []);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setDoc(null);
    setForm(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = selectedPrac ? { practitioner_id: selectedPrac } : {};
      const r = await api.post("/documents/upload", fd, {
        params,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDoc(r.data);
      const o = r.data.ocr_data || {};
      setForm({
        practitioner_id: selectedPrac || "",
        credential_type: (o.document_type && CRED_TYPES.includes(o.document_type)) ? o.document_type : "STATE_LICENSE",
        credential_number: o.credential_number || "",
        issuer: o.issuer || "",
        issue_date: o.issue_date || "",
        expiration_date: o.expiration_date || "",
        psv_status: "PENDING",
        notes: o.raw_text_snippet ? `OCR: ${String(o.raw_text_snippet).slice(0, 200)}` : "",
        document_id: r.data.id,
      });
      if (o.error) toast.warning("OCR partial: " + o.error);
      else toast.success("Document uploaded — OCR complete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = useCallback((files) => handleFile(files[0]), [selectedPrac]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpg", ".jpeg", ".png", ".webp"],
      "application/pdf": [".pdf"],
    },
    multiple: false,
  });

  const save = async () => {
    if (!form.practitioner_id) return toast.error("Assign a practitioner first");
    setSaving(true);
    try {
      await api.post("/credentials", form);
      toast.success("Credential created from OCR data");
      setDoc(null); setForm(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="upload-page">
      <header className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
        <div>
          <div className="overline text-gray-500">Document intake</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 mt-1">Upload &amp; OCR</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-2xl">
            Drop scanned licenses, DEA certificates, board cards, BLS/ACLS, malpractice certificates, or immunization records.
            GPT-5.2 Vision extracts credential number, issuer, and expiration so you can file it in one click.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        <div className="bg-white border border-gray-200 p-6">
          <div className="overline text-gray-500">Step 1</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Assign to practitioner (optional)</h2>
          <select
            value={selectedPrac}
            onChange={(e) => setSelectedPrac(e.target.value)}
            className="w-full border border-gray-300 bg-white px-3 py-2 text-sm mb-6"
            data-testid="upload-practitioner-select"
          >
            <option value="">— Assign later —</option>
            {practitioners.map((p) => (
              <option key={p.id} value={p.id}>{p.last_name}, {p.first_name} ({p.title})</option>
            ))}
          </select>

          <div className="overline text-gray-500 mt-4">Step 2</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Drop a credential document</h2>

          <div
            {...getRootProps()}
            data-testid="upload-dropzone"
            className={`border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
              isDragActive ? "border-slate-900 bg-slate-50" : "border-gray-300 hover:border-slate-900 bg-gray-50"
            }`}
          >
            <input {...getInputProps()} />
            <CloudArrowUp size={40} weight="duotone" className="mx-auto text-slate-900" />
            <div className="font-heading text-lg font-semibold text-slate-900 mt-4">
              {uploading ? "Extracting…" : isDragActive ? "Drop to upload" : "Drop file here or click to browse"}
            </div>
            <div className="text-xs text-gray-500 mt-2 font-mono uppercase tracking-wider">PDF · JPG · PNG · WEBP</div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-6">
          <div className="overline text-gray-500">OCR Preview</div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mt-0.5 mb-4">Extracted fields</h2>
          {!doc && (
            <div className="border border-dashed border-gray-300 p-8 bg-gray-50">
              <img
                src="https://images.pexels.com/photos/8413295/pexels-photo-8413295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Credential document"
                className="w-full max-h-60 object-cover mb-4"
              />
              <div className="text-sm text-gray-500 text-center">
                Upload a document to see extracted credential fields here.
              </div>
            </div>
          )}
          {doc && form && (
            <div className="space-y-3" data-testid="ocr-preview">
              <div className="flex items-center gap-2 text-xs font-mono bg-emerald-50 text-emerald-800 px-3 py-2 border border-emerald-200">
                <CheckCircle size={14} weight="bold" /> {doc.original_filename}
              </div>
              <FieldEdit label="Practitioner" testid="ocr-practitioner">
                <select value={form.practitioner_id} onChange={(e) => setForm({ ...form, practitioner_id: e.target.value })} className="w-full border border-gray-300 bg-white px-3 py-2 text-sm">
                  <option value="">— Select —</option>
                  {practitioners.map((p) => <option key={p.id} value={p.id}>{p.last_name}, {p.first_name} ({p.title})</option>)}
                </select>
              </FieldEdit>
              <FieldEdit label="Credential type">
                <select value={form.credential_type} onChange={(e) => setForm({ ...form, credential_type: e.target.value })} className="w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="ocr-cred-type">
                  {CRED_TYPES.map((t) => <option key={t} value={t}>{CRED_LABELS[t]}</option>)}
                </select>
              </FieldEdit>
              <FieldEdit label="Credential number">
                <input value={form.credential_number} onChange={(e) => setForm({ ...form, credential_number: e.target.value })} className="w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="ocr-cred-number" />
              </FieldEdit>
              <FieldEdit label="Issuer">
                <input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} className="w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="ocr-issuer" />
              </FieldEdit>
              <div className="grid grid-cols-2 gap-3">
                <FieldEdit label="Issue date">
                  <input type="date" value={(form.issue_date || "").slice(0,10)} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="ocr-issue-date" />
                </FieldEdit>
                <FieldEdit label="Expiration date">
                  <input type="date" value={(form.expiration_date || "").slice(0,10)} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} className="w-full border border-gray-300 bg-white px-3 py-2 text-sm" data-testid="ocr-exp-date" />
                </FieldEdit>
              </div>
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
                <button onClick={() => { setDoc(null); setForm(null); }} className="border border-gray-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-gray-100">Discard</button>
                <button onClick={save} disabled={saving} className="bg-slate-900 text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-slate-700 inline-flex items-center gap-2 disabled:opacity-60" data-testid="ocr-save-credential-btn">
                  <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "File credential"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldEdit({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
